import moment from "moment";

/** Company timezone — India Standard Time (UTC+5:30). */
export const IST_OFFSET = "+05:30";

const MYSQL_DATETIME_RE = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;

/**
 * Extract YYYY-MM-DD HH:mm:ss wall-clock from API/DB values.
 * MySQL DATETIME is stored as IST wall-clock; the driver may expose it as a UTC Date/ISO string
 * with the same clock digits — we must NOT add +5:30 again.
 */
export const extractWallClockDateTime = (value) => {
  if (value == null || value === "") return null;

  if (value instanceof Date) {
    const p = (n) => String(n).padStart(2, "0");
    return `${value.getUTCFullYear()}-${p(value.getUTCMonth() + 1)}-${p(value.getUTCDate())} ${p(value.getUTCHours())}:${p(value.getUTCMinutes())}:${p(value.getUTCSeconds())}`;
  }

  const raw = String(value).trim();
  if (!raw) return null;

  const match = raw.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2})/);
  if (match) return `${match[1]} ${match[2]}`;

  if (MYSQL_DATETIME_RE.test(raw)) return raw;

  return null;
};

/**
 * Parse API/DB datetime as IST wall-clock (Delhi).
 */
export const parseAppDateTime = (value) => {
  const wall = extractWallClockDateTime(value);
  if (!wall) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(wall)) {
    const m = moment(wall, "YYYY-MM-DD", true).utcOffset(IST_OFFSET, true);
    return m.isValid() ? m : null;
  }

  const m = moment(wall, "YYYY-MM-DD HH:mm:ss", true).utcOffset(IST_OFFSET, true);
  return m.isValid() ? m : null;
};

/** Current moment in IST. */
export const nowIst = () => moment().utcOffset(IST_OFFSET);

/**
 * Format datetime for display in IST (Delhi).
 */
export const formatDateTimeIst = (value, pattern = "D MMM YYYY, h:mm A ") => {
  const m = parseAppDateTime(value);
  if (!m) return "—";
  return m.format(pattern);
};

/** Today's date string (YYYY-MM-DD) in IST. */
export const todayIstDateStr = () => nowIst().format("YYYY-MM-DD");

/**
 * Format duration as "X hrs Y mins".
 */
export const formatWorkingDuration = (value, { fromMinutes = false } = {}) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const totalMinutes = fromMinutes
    ? Math.max(0, Math.round(Number(value)))
    : Math.max(0, Math.round(Number(value) * 60));
  const hrs = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${hrs} hrs ${mins} mins`;
};

/** Minutes between two API datetimes (IST wall-clock). */
export const diffMinutesAppDateTime = (startValue, endValue = nowIst()) => {
  const start = parseAppDateTime(startValue);
  const end = moment.isMoment(endValue) ? endValue : parseAppDateTime(endValue);
  if (!start?.isValid() || !end?.isValid()) return 0;
  return Math.max(0, Math.round(end.diff(start, "minutes", true)));
};


/** Resolve working minutes for a session row (API value or computed from timestamps). */
export const resolveSessionWorkingMinutes = (row, { liveMinutes = null, activeSessionId = null } = {}) => {
  if (!row) return null;
  if (row.status === "active" && row.id === activeSessionId && liveMinutes != null) {
    return liveMinutes;
  }
  if (row.totalWorkingMinutes != null && Number(row.totalWorkingMinutes) > 0) {
    return Math.round(Number(row.totalWorkingMinutes));
  }
  if (row.checkInAt && row.checkOutAt) {
    const gross = diffMinutesAppDateTime(row.checkInAt, row.checkOutAt);
    const paused = Math.round(Number(row.totalPausedMinutes) || 0);
    return Math.max(0, gross - paused);
  }
  if (row.checkInAt && row.status === "active") {
    let gross = diffMinutesAppDateTime(row.checkInAt, nowIst());
    const paused = Math.round(Number(row.totalPausedMinutes) || 0);
    if (row.isPaused && row.pausedAt) {
      gross -= diffMinutesAppDateTime(row.pausedAt, nowIst());
    }
    return Math.max(0, gross - paused);
  }
  return row.totalWorkingMinutes != null ? Math.round(Number(row.totalWorkingMinutes)) : null;
};

/** Sum working minutes for all sessions that started today (IST). */
export const computeTodayTotalWorkingMinutes = (
  sessions,
  { liveMinutes = null, activeSessionId = null, today = null } = {}
) => {
  const todayStr = today || todayIstDateStr();
  return (sessions || []).reduce((sum, row) => {
    const wall = extractWallClockDateTime(row.checkInAt);
    if (!wall?.startsWith(todayStr)) return sum;
    const mins = resolveSessionWorkingMinutes(row, { liveMinutes, activeSessionId });
    return sum + (mins || 0);
  }, 0);
};
